import { Component, Input, EventEmitter, Output } from '@angular/core';
import { SortType, SurveyUserTypes } from '../constants-types/Enums';
import { Person } from '../core-data-model/Person.model';
import { SearchResult } from '../core-data-model/SearchResult.model';
import { SearchResultSet } from '../core-data-model/SearchResultSet';
import { Table } from '../core-services/Tables/Table';
import { DateFormatUtility } from '../core-utilities/DateFormat.utility';
import { ModuleAccessService } from '../system-services/ModuleAccess/ModuleAccess.service';
import { UserProfileSettingsService } from '../system-services/UserProfileSettings/UserProfileSettings.service';
import { TranslationService } from '../translations';

@Component({
    selector: 'results-table',
    template: `
        <pi-table
            [table]="tableData"
            [selection]="selection"
            (selectionChanged)="updateSelection($event)"
        >
            <ng-template #header>
                <th
                    [register-column]="{
                        columnId: 'resultType',
                        headerKey: 'common.ResultType'
                    }"
                    class="pi-table_cell--iconSize pi-table_cell--centered"
                ></th>
                <th
                    [register-column]="{
                        columnId: 'resultName',
                        headerKey: 'label.common-terms.name'
                    }"
                    [sortable-header]="SortType.Name"
                    class="pi-table_cell--textLeftAligned pi-table_cell--largeSize"
                >
                    {{ TR.key('label.common-terms.name') }}
                </th>
                <th
                    [register-column]="{
                        columnId: 'email',
                        headerKey: 'label.email'
                    }"
                >
                    {{ TR.key('label.email') }}
                </th>
                @if (isAllowedForModule()) {
                    <th
                        [register-column]="{
                            columnId: 'folder',
                            headerKey: 'label.common-terms.folder',
                            defaultHide: true
                        }"
                        class="pi-table_cell--mediumSize"
                    >
                        {{ TR.key('label.common-terms.folder') }}
                    </th>
                }
                <th
                    [register-column]="{
                        columnId: 'resultJobTitle',
                        headerKey: 'label.common-terms.job'
                    }"
                    class="pi-table_cell--textLeftAligned"
                    [sortable-header]="SortType.AssociatedJob"
                >
                    {{ TR.key('label.common-terms.job') }}
                </th>
                <th
                    [register-column]="{
                        columnId: 'lastUpdated',
                        headerKey: 'label.common-terms.last.modified'
                    }"
                    [sortable-header]="SortType.LastUpdated"
                >
                    {{ TR.key('label.common-terms.last.modified') }}
                </th>
                <th
                    [register-column]="{
                        columnId: 'personType',
                        headerKey: 'label.common-terms.type'
                    }"
                    class="pi-table_cell--rightBorder"
                    [sortable-header]="SortType.PersonType"
                >
                    {{ TR.key('label.common-terms.type') }}
                </th>
                <th
                    [register-column]="{
                        columnId: 'resultBehaviorChart',
                        headerKey: 'label.common-terms.behavioral'
                    }"
                    class="pi-table_cell--mediumSize"
                >
                    {{ TR.key('label.common-terms.behavioral') }}
                </th>
                <th
                    [register-column]="{
                        columnId: 'resultBehaviorName',
                        headerKey: 'label.common-terms.ref-pattern'
                    }"
                    [sortable-header]="SortType.ReferenceProfile"
                    class="pi-table_cell--rightBorder"
                >
                    {{ TR.key('label.common-terms.ref-pattern') }}
                    <span
                        tooltip
                        [tooltipContent]="
                            TR.key('table.referencePatternColumnTooltip')
                        "
                        [tooltipDisabled]="false"
                        [tooltipAnimation]="true"
                        tooltipPlacement="top"
                    >
                        <i
                            class="clickable fa fa-info-circle v-align-middle"
                            style="padding-left:6px"
                        ></i>
                    </span>
                </th>
                @if (userProfile.showCognitive) {
                    <th
                        [register-column]="{
                            columnId: 'resultCognitive',
                            headerKey: 'label.common-terms.cognitive'
                        }"
                        class="pi-table_cell--mediumSize"
                    >
                        {{ TR.key('label.common-terms.cognitive') }}
                    </th>
                }
                <th
                    [register-column]="{
                        columnId: 'resultBehaviorScoreId',
                        headerKey: 'PatternCode',
                        defaultHide: true
                    }"
                >
                    {{ TR.key('PatternCode') }}
                </th>
                <th
                    [register-column]="{
                        columnId: 'administeredByBehavioral',
                        headerKey: 'table.column-administeredByBehavioral',
                        defaultHide: true
                    }"
                    class="pi-table_cell--mediumSize"
                >
                    {{ TR.key('table.column-administeredByBehavioral') }}
                </th>
                @if (isAllowedForModule()) {
                    <th
                        [register-column]="{
                            columnId: 'administeredByCognitive',
                            headerKey: 'table.column-administeredByCognitive',
                            defaultHide: true
                        }"
                        class="pi-table_cell--mediumSize"
                    >
                        {{ TR.key('table.column-administeredByCognitive') }}
                    </th>
                }
            </ng-template>
            <ng-template #dataRow let-item="item">
                <td
                    show-column="resultType"
                    class="pi-table_cell--iconSize pi-table_cell--centered"
                >
                    <table-icon [result]="item"></table-icon>
                </td>
                <td
                    show-column="resultName"
                    class="pi-table_cell--textLeftAligned pi-table_cell--largeSize"
                >
                    <result-link
                        [result]="item"
                        data-testing="resultsLink"
                    ></result-link>
                </td>
                <td show-column="email">
                    {{ item.email || '' }}
                </td>
                @if (isAllowedForModule()) {
                    <td show-column="folder" class="pi-table_cell--mediumSize">
                        {{ item.folder.folderPath }}
                    </td>
                }
                <td
                    show-column="resultJobTitle"
                    class="pi-table_cell--textLeftAligned"
                >
                    <job-link [result]="item"></job-link>
                </td>
                <td show-column="lastUpdated">
                    {{ dateUtil.transformDate(item.lastUpdated) }}
                </td>
                <td show-column="personType" class="pi-table_cell--rightBorder">
                    {{ TR.key(SurveyUserTypes[item.type]) }}
                </td>
                <td
                    show-column="resultBehaviorChart"
                    class="pi-table_cell--mediumSize"
                >
                    <behavioral-thumb
                        [result]="item"
                        (dataChange)="tableData.waitAndReload()"
                    ></behavioral-thumb>
                </td>
                <td
                    show-column="resultBehaviorName"
                    class="pi-table_cell--rightBorder"
                >
                    <span
                        tooltip
                        [tooltipContent]="
                            TR.key(tableData.getTooltipContentKey(item))
                        "
                        [tooltipAnimation]="true"
                        tooltipPlacement="top"
                    >
                        {{
                            item.referencePattern
                                ? TR.key(item.referencePattern.key)
                                : ''
                        }}
                    </span>
                </td>
                @if (userProfile.showCognitive) {
                    <td
                        show-column="resultCognitive"
                        class="pi-table_cell--mediumSize"
                    >
                        <cognitive-thumb
                            [result]="item"
                            (dataChange)="tableData.waitAndReload()"
                        ></cognitive-thumb>
                    </td>
                }
                <td show-column="resultBehaviorScoreId">
                    {{ item?.originalAssessment?.scoreId || '' }}
                </td>
                <td
                    show-column="administeredByBehavioral"
                    class="pi-table_cell--mediumSize"
                >
                    {{ item?.behavioralAssessmentAdministeredBy || '' }}
                </td>
                @if (isAllowedForModule()) {
                    <td
                        show-column="administeredByCognitive"
                        class="pi-table_cell--mediumSize"
                    >
                        {{ displayCognitiveAdministeredBy(item) }}
                    </td>
                }
            </ng-template>
        </pi-table>
    `,
    standalone: false
})
export class ResultsTableComponent {
    SurveyUserTypes = SurveyUserTypes;
    SortType = SortType;

    @Input()
    tableData: Table<SearchResult>;
    @Input()
    selection: SearchResultSet; // This should probably change

    @Output()
    selectionChanged = new EventEmitter<SearchResultSet>();

    constructor(
        public userProfile: UserProfileSettingsService,
        public dateUtil: DateFormatUtility,
        public TR: TranslationService,
        public moduleAccessService: ModuleAccessService
    ) {}

    isAllowedForModule() {
        const isInspireAndSelfServe =
            this.moduleAccessService.hasInspire &&
            this.moduleAccessService.hasAnySelfServeModule;
        return !isInspireAndSelfServe;
    }

    updateSelection(newSelection: SearchResultSet) {
        this.selectionChanged.emit(newSelection);
    }

    displayCognitiveAdministeredBy(item: SearchResult): string {
        return item && item instanceof Person
            ? item.getCognitiveAdministeredByForDisplay()
            : '';
    }
}
